/**
 * Every state the Operations page can be in has a URL (DASHBOARD.md §1.13), so a
 * view can be pasted into a message and a refresh lands on the same screen.
 *
 *   ?company=quill&bot=quill-budget&tab=verdict&action=<id>&ga=GA-21
 *   &status=yellow&exec=CFO&decision=pending&sort=updated&dir=desc
 *   &team=CFO&area=Growth%20%26%20Marketing   (Action Center filters, separate from the table's exec)
 *   &bucket=Subscription&impl=done            (Action Center: impact bucket, implementation status)
 *
 * The Action Center opens on Pending: `decision` is absent for pending and written
 * as `decision=all` when Grant asks for everything.
 *
 * The URL is the single source of truth; this hook only parses it (forgiving: an
 * unknown value falls back to the default, it never throws) and writes it.
 * Opening a bot or item uses push, so Back closes the drawer; everything else
 * (filters, sorting, closing) uses replace, so the history does not fill up.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Company, EXEC_ORDER } from '../lib/types';
import { Bucket, DECISION_FILTERS, DEFAULT_DECISION, DecisionFilter, IMPL_FILTERS, ImplFilter, SORT_KEYS, SortDir, SortKey, StatusFilter, isBucket, isCompany } from '../lib/selectors';

export const DRAWER_TABS = ['inbox', 'actions', 'verdict', 'findings', 'detail'] as const;
export type DrawerTab = (typeof DRAWER_TABS)[number];

export interface BotOpsUrl {
  company: Company;
  bot: string | null;
  tab: DrawerTab;
  action: string | null;
  ga: string | null;
  status: StatusFilter;
  exec: string | null;
  /** Action Center: one C-suite line (an EXEC_ORDER label). */
  team: string | null;
  /** Action Center: one org-chart area (a cluster name); checked against the company's clusters where it is used. */
  area: string | null;
  /** Action Center: one impact bucket (Subscription, Security, Everything else, Unbucketed). */
  bucket: Bucket | null;
  /** Action Center: one implementation status (the digest's "implementations done" link uses done). */
  impl: ImplFilter | null;
  decision: DecisionFilter;
  sort: SortKey;
  dir: SortDir;
  panel: string | null;
  instruction: string | null;
  /** True when the URL asks for a drawer: an item, or a bot together with a tab. */
  drawerOpen: boolean;
}

export type UrlUpdates = Partial<Record<'company' | 'bot' | 'tab' | 'action' | 'ga' | 'status' | 'exec' | 'team' | 'area' | 'bucket' | 'impl' | 'decision' | 'sort' | 'dir' | 'panel' | 'instruction', string | null>>;

const oneOf = <T extends string>(v: string | null, list: readonly T[], fallback: T): T =>
  v && (list as readonly string[]).includes(v) ? (v as T) : fallback;

/** A conservative id/slug check: lowercase letters, digits, dashes, underscores, dots. */
const safeId = (v: string | null): string | null => (v && /^[A-Za-z0-9][A-Za-z0-9._-]{0,80}$/.test(v) ? v : null);

export function parseBotOpsUrl(sp: URLSearchParams): BotOpsUrl {
  const company = isCompany(sp.get('company')) ? (sp.get('company') as Company) : 'lifescribe';
  const status = oneOf(sp.get('status'), ['green', 'yellow', 'red'] as const, 'none' as never);
  const exec = sp.get('exec');
  const team = sp.get('team');
  const area = sp.get('area');
  const bot = safeId(sp.get('bot'));
  const action = safeId(sp.get('action'));
  return {
    company,
    bot,
    tab: oneOf(sp.get('tab'), DRAWER_TABS, 'inbox'),
    action,
    ga: safeId(sp.get('ga')),
    status: (status as unknown as string) === 'none' ? null : status,
    exec: exec && (EXEC_ORDER as readonly string[]).includes(exec) ? exec : null,
    team: team && (EXEC_ORDER as readonly string[]).includes(team) ? team : null,
    area: area && area.length <= 80 && /^[A-Za-z0-9 &,.()'/+-]+$/.test(area) ? area : null,
    bucket: isBucket(sp.get('bucket')) ? (sp.get('bucket') as Bucket) : null,
    impl: (IMPL_FILTERS as readonly string[]).includes(sp.get('impl') || '') ? (sp.get('impl') as ImplFilter) : null,
    decision: oneOf(sp.get('decision'), DECISION_FILTERS, DEFAULT_DECISION),
    sort: oneOf(sp.get('sort'), SORT_KEYS, 'status'),
    dir: oneOf(sp.get('dir'), ['asc', 'desc'] as const, 'asc'),
    panel: sp.get('panel') === 'sources' ? 'sources' : null,
    instruction: safeId(sp.get('instruction')),
    // ?bot=x&tab=y opens the bot drawer; ?bot=x alone only filters the Action Center
    // (the spec's "Show in Action Center" link, drawer closed); ?action=id opens the item.
    drawerOpen: Boolean(action || (bot && sp.has('tab'))),
  };
}

/** Switching company starts clean: no selection, filters or open drawer carry over. */
const COMPANY_RESETS = ['bot', 'tab', 'action', 'ga', 'status', 'exec', 'team', 'area', 'bucket', 'impl', 'decision', 'sort', 'dir', 'panel', 'instruction'] as const;

export function useBotOpsUrlState(): [BotOpsUrl, (updates: UrlUpdates, opts?: { push?: boolean }) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const url = useMemo(() => parseBotOpsUrl(searchParams), [searchParams]);

  const update = useCallback((updates: UrlUpdates, opts: { push?: boolean } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const apply = (k: string, v: string | null | undefined) => {
        if (v === null || v === undefined || v === '') next.delete(k);
        else next.set(k, v);
      };
      if ('company' in updates) COMPANY_RESETS.forEach((k) => next.delete(k));
      Object.entries(updates).forEach(([k, v]) => apply(k, v));
      // Default values are never written, so the plain page has a plain URL.
      if (next.get('company') === 'lifescribe') next.delete('company');
      if (next.get('decision') === DEFAULT_DECISION) next.delete('decision');
      if (next.get('tab') === 'inbox' && !next.get('bot') && !next.get('action')) next.delete('tab');
      if (next.get('sort') === 'status' && (next.get('dir') || 'asc') === 'asc') { next.delete('sort'); next.delete('dir'); }
      return next;
    }, { replace: !opts.push });
  }, [setSearchParams]);

  return [url, update];
}
